import { DEG } from './math';
import type { ParamSchema } from './params/types';
import type { ParamValues } from './params/types';
import { createRng } from './rng';

/**
 * Canvas background.
 *
 * Schema-driven like everything else, so the batch engine can vary it across a run
 * without any special handling.
 */
export const BACKGROUND_SCHEMA = [
  {
    kind: 'enum',
    key: 'kind',
    label: 'Typ',
    default: 'solid',
    group: 'Hintergrund',
    options: [
      { value: 'solid', label: 'Vollton' },
      { value: 'linear', label: 'Linearer Verlauf' },
      { value: 'radial', label: 'Radialer Verlauf' },
      { value: 'noise', label: 'Rauschtextur' },
      { value: 'transparent', label: 'Transparent' },
    ],
    tooltip: 'Transparent lässt den Hintergrund im PNG-Export offen.',
  },
  {
    kind: 'color',
    key: 'colorA',
    label: 'Farbe A',
    default: '#0b0b0e',
    group: 'Hintergrund',
    showIf: (v) => v.kind !== 'transparent',
  },
  {
    kind: 'color',
    key: 'colorB',
    label: 'Farbe B',
    default: '#1c2a3a',
    group: 'Hintergrund',
    showIf: (v) => v.kind === 'linear' || v.kind === 'radial' || v.kind === 'noise',
  },
  {
    kind: 'float',
    key: 'angle',
    label: 'Winkel',
    min: -180,
    max: 180,
    step: 1,
    default: 45,
    unit: '°',
    group: 'Hintergrund',
    showIf: (v) => v.kind === 'linear',
  },
  {
    kind: 'float',
    key: 'radius',
    label: 'Radius',
    min: 0.1,
    max: 2,
    step: 0.01,
    default: 0.8,
    unit: '×',
    group: 'Hintergrund',
    tooltip: 'Ausdehnung des radialen Verlaufs, relativ zur Bilddiagonale.',
    showIf: (v) => v.kind === 'radial',
  },
  {
    kind: 'vec2',
    key: 'center',
    label: 'Zentrum',
    min: { x: -0.5, y: -0.5 },
    max: { x: 1.5, y: 1.5 },
    step: 0.005,
    default: { x: 0.5, y: 0.42 },
    square: false,
    group: 'Hintergrund',
    showIf: (v) => v.kind === 'radial',
  },
  {
    kind: 'float',
    key: 'noiseScale',
    label: 'Körnung',
    min: 0.5,
    max: 24,
    step: 0.5,
    default: 3,
    unit: 'px',
    group: 'Hintergrund',
    tooltip: 'Grösse eines Rauschkorns in Pixeln.',
    showIf: (v) => v.kind === 'noise',
  },
  {
    kind: 'float',
    key: 'noiseAmount',
    label: 'Rauschstärke',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.35,
    group: 'Hintergrund',
    showIf: (v) => v.kind === 'noise',
  },
] as const satisfies ParamSchema;

export const BACKGROUND_PARAMS: ParamSchema = BACKGROUND_SCHEMA;

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export function drawBackground(ctx: Ctx2D, values: ParamValues, width: number, height: number, seed: number): void {
  const kind = typeof values.kind === 'string' ? values.kind : 'solid';
  const colorA = typeof values.colorA === 'string' ? values.colorA : '#0b0b0e';
  const colorB = typeof values.colorB === 'string' ? values.colorB : '#1c2a3a';

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.clearRect(0, 0, width, height);

  switch (kind) {
    case 'transparent':
      break;

    case 'linear': {
      const angle = (typeof values.angle === 'number' ? values.angle : 45) * DEG;
      // Project the gradient axis onto the canvas so the stops always span the whole
      // image, whatever the angle.
      const cx = width / 2;
      const cy = height / 2;
      const len = Math.abs(width * Math.cos(angle)) + Math.abs(height * Math.sin(angle));
      const dx = (Math.cos(angle) * len) / 2;
      const dy = (Math.sin(angle) * len) / 2;
      const g = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
      g.addColorStop(0, colorA);
      g.addColorStop(1, colorB);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, width, height);
      break;
    }

    case 'radial': {
      const center = (values.center as { x: number; y: number }) ?? { x: 0.5, y: 0.42 };
      const factor = typeof values.radius === 'number' ? values.radius : 0.8;
      const r = Math.hypot(width, height) * factor;
      const g = ctx.createRadialGradient(center.x * width, center.y * height, 0, center.x * width, center.y * height, r);
      g.addColorStop(0, colorA);
      g.addColorStop(1, colorB);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, width, height);
      break;
    }

    case 'noise': {
      ctx.fillStyle = colorA;
      ctx.fillRect(0, 0, width, height);
      drawNoiseTexture(ctx, values, width, height, seed, colorB);
      break;
    }

    default:
      ctx.fillStyle = colorA;
      ctx.fillRect(0, 0, width, height);
  }

  ctx.restore();
}

/**
 * Value-noise texture, rendered by drawing a small noise tile and scaling it up.
 *
 * Generating noise at full resolution and pushing it through `putImageData` costs a
 * full-canvas allocation on every redraw; a tile the size of a few grains, scaled with
 * smoothing on, gives the same soft cloudiness for a fraction of the work.
 */
function drawNoiseTexture(
  ctx: Ctx2D,
  values: ParamValues,
  width: number,
  height: number,
  seed: number,
  color: string,
): void {
  const grain = Math.max(0.5, typeof values.noiseScale === 'number' ? values.noiseScale : 3);
  const amount = typeof values.noiseAmount === 'number' ? values.noiseAmount : 0.35;
  if (amount <= 0) return;

  const tw = Math.max(2, Math.ceil(width / grain));
  const th = Math.max(2, Math.ceil(height / grain));

  const tile = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(tw, th) : document.createElement('canvas');
  if (!(tile instanceof OffscreenCanvas)) {
    tile.width = tw;
    tile.height = th;
  }
  const tctx = tile.getContext('2d') as Ctx2D | null;
  if (!tctx) return;

  const rng = createRng(seed);
  const img = tctx.createImageData(tw, th);
  const d = img.data;
  const base = parseColorBytes(color);
  for (let i = 0; i < tw * th; i++) {
    const n = rng.next();
    d[i * 4] = base[0];
    d[i * 4 + 1] = base[1];
    d[i * 4 + 2] = base[2];
    d[i * 4 + 3] = Math.round(n * 255 * amount);
  }
  tctx.putImageData(img, 0, 0);

  ctx.imageSmoothingEnabled = grain > 1.5;
  ctx.drawImage(tile as CanvasImageSource, 0, 0, width, height);
  ctx.imageSmoothingEnabled = true;
}

function parseColorBytes(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean.padEnd(6, '0').slice(0, 6);
  const n = Number.parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
